'use client';

import { For, Icon, SegmentGroup } from '@chakra-ui/react';
import { Check } from 'lucide-react';
import { forwardRef, useMemo } from 'react';

interface Item {
    value: string;
    label: React.ReactNode;
    disabled?: boolean;
}

export interface SegmentedControlProps extends SegmentGroup.RootProps {
    items: Array<string | Item>;
}

function normalize(items: Array<string | Item>): Item[] {
    return items.map((item) => {
        if (typeof item === 'string') {
            return { value: item, label: item };
        }
        return item;
    });
}

export const SegmentedControl = forwardRef<HTMLDivElement, SegmentedControlProps & { deselectable?: boolean }>(
    function SegmentedControl(props, ref) {
        const { deselectable, value, onClick, items, onValueChange } = props;
        const data = useMemo(() => normalize(items), [items]);

        return (
            <SegmentGroup.Root
                {...props}
                ref={ref}
                onClick={(e) => {
                    if (deselectable && (e.target as HTMLInputElement).value === value) {
                        onValueChange?.({ value: '' });
                    }
                    onClick?.(e);
                }}
                colorPalette="purple"
                css={{
                    '--chakra-colors-bg': 'colors.purple.100',
                    '--chakra-colors-bg-emphasized': 'colors.colorPalette.100',
                    '--chakra-colors-bg-muted': 'rgba(0, 0, 0, 0.02)',
                    '& > [data-state=checked]': { _dark: { '--chakra-colors-fg': 'colors.musa.light.100' } },
                    '& > [data-part=root]': { borderRadius: '4px' },
                }}
                h="36px">
                <SegmentGroup.Indicator />
                <For each={data}>
                    {(item) => (
                        <SegmentGroup.Item key={item.value} value={item.value} disabled={item.disabled}>
                            <SegmentGroup.ItemText fontSize="12px">
                                {item.label?.toString().toLowerCase() === value?.toLowerCase() ? (
                                    <Icon boxSize="20px" stroke="musa.light.100" pr="1">
                                        <Check />
                                    </Icon>
                                ) : null}
                                {item.label}
                            </SegmentGroup.ItemText>
                            <SegmentGroup.ItemHiddenInput />
                        </SegmentGroup.Item>
                    )}
                </For>
            </SegmentGroup.Root>
        );
    },
);
